import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo, Wordmark } from "./Logo";

const links = [
  { label: "Services", href: "#services" },
  { label: "Trades", href: "#trades" },
  { label: "Work", href: "#work" },
  { label: "Reviews", href: "#reviews" },
  { label: "Pricing", href: "#pricing" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="agency-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-obsidian/80 backdrop-blur-xl border-b gold-border-subtle" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-3 group" data-testid="nav-logo-button">
          <motion.div whileHover={{ rotate: 90 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
            <Logo size={38} />
          </motion.div>
          <Wordmark />
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-gold transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:block">
          <button
            data-testid="nav-cta-button"
            onClick={() => go("#contact")}
            className="group flex items-center gap-2 px-5 py-2.5 rounded-full border border-gold/40 text-gold font-mono text-xs uppercase tracking-[0.15em] hover:bg-gold hover:text-obsidian transition-all duration-300"
          >
            Request Proposal
            <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
          </button>
        </div>

        <button data-testid="nav-mobile-toggle" className="md:hidden text-gold" onClick={() => setOpen(!open)}>
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-obsidian/95 backdrop-blur-xl border-b gold-border-subtle overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="text-left font-mono text-sm uppercase tracking-[0.2em] text-zinc-300 hover:text-gold transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-cta"
                onClick={() => go("#contact")}
                className="mt-2 px-5 py-3 rounded-full bg-gold text-obsidian font-mono text-xs uppercase tracking-[0.15em] font-medium"
              >
                Request Proposal
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
